import { set } from 'lodash/fp';

import { VerificationTemplateVersionDiff } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionGovernmentIdAttributes,
  VerificationTemplateVersionSchema,
  VerificationTemplateVersionStatus,
} from 'dashboard/models/verification-template-version';
import {
  setLatestPublishedVerificationTemplateVersion,
  setMatchRequirementProfiles,
  setVerificationTemplate,
  setVerificationTemplateSchema,
  setVerificationTemplateVersion,
  setVerificationTemplateVersionDiff,
} from 'dashboard/redux/slices/verification-template';
import { isVerificationTemplateVersion } from 'dashboard/redux/thunk-actions/verification-templates';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ReferencedIdTuple } from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import { ApiEndpoint, ApiModel, ApiResource } from 'lib/interfaces/api';

const BASE_PATH = '/dashboard/verification-templates';

export const getEditorPath = (templateId: string, versionId: string): string =>
  `${BASE_PATH}/${templateId}/${versionId}`;

// Verification Template Version
export function loadVerificationTemplateVersion(id: string): DashboardThunkAction {
  return async (dispatch) => {
    const { data: verificationTemplateVersion, included } =
      await SessionClientDashboard.getResource<VerificationTemplateVersion>(
        ApiEndpoint.VerificationTemplateVersion,
        id,
      );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const verificationTemplate = included.find((entity): entity is VerificationTemplate =>
      isVerificationTemplate(entity),
    );
    dispatch(
      setVerificationTemplateVersion({ verificationTemplateVersion: verificationTemplateVersion }),
    );
    dispatch(setVerificationTemplate({ verificationTemplate: verificationTemplate }));
  };
}

export function loadLatestPublishedVerificationTemplateVersion(id: string): DashboardThunkAction {
  return async (dispatch) => {
    const { data: verificationTemplateVersion } =
      await SessionClientDashboard.getResource<VerificationTemplateVersion>(
        ApiEndpoint.VerificationTemplateVersion,
        id,
      );

    dispatch(
      setLatestPublishedVerificationTemplateVersion({
        verificationTemplateVersion: verificationTemplateVersion,
      }),
    );
  };
}

/**
 * Used in DashboardVerificationTemplateShowRoute -
 * Loads the related inquiry template versions (just the tokens)
 * This is to help determine whether the VTV should be editable in the VTE
 */
export function loadVerificationTemplateSchema(id: string): DashboardThunkAction {
  return async (dispatch) => {
    const schema = await SessionClientDashboard.get<VerificationTemplateVersionSchema>(
      ApiEndpoint.VerificationTemplateVersion,
      id,
      'schema',
    );
    dispatch(setVerificationTemplateSchema({ schema: schema }));
  };
}

/**
 * Used in DashboardVerificationTemplateShowRoute -
 * Loads match requirement profiles for a verification template version
 * Currently the match requirement profiles are static, but they will be dynamic in the future
 */
export function loadMatchRequirementProfiles(id: string): DashboardThunkAction {
  return async (dispatch) => {
    const matchRequirementProfiles = await SessionClientDashboard.get<MatchRequirementProfile[]>(
      ApiEndpoint.VerificationTemplateVersion,
      id,
      'match-requirement-profiles',
    );
    dispatch(setMatchRequirementProfiles({ matchRequirementProfiles: matchRequirementProfiles }));
  };
}

/**
 * Update used in FLE and VTE -
 * returns id tuple if new version created to update referenced ids
 */
export function saveVtmplv(
  vtmplv: VerificationTemplateVersion,
): DashboardThunkAction<Promise<ReferencedIdTuple>> {
  const vtmplId = vtmplv.relationships['verification-template']?.data?.id;
  return async () => {
    const updatedVersion = vtmplId
      ? await saveVerificationTemplateVersion(vtmplId, vtmplv)
      : await createVerificationTemplate(vtmplv);
    // return updated id tuple to update references in template editor
    return { originalId: vtmplv.id, updatedId: updatedVersion.id };
  };
}

/**
 * Currently used only in FLE and VTE when inserting new VTVs via modules.
 * Since neither the VTV nor the parent template exist on the server yet,
 * we need to first create the template, then pass the vtmplv *from the function params*
 * on to saveVerificationTemplateVersion to update the newly created VTV with the correct attributes from the module.
 */
export async function createVerificationTemplate(
  updatedVtmplv: VerificationTemplateVersion,
): Promise<VerificationTemplateVersion> {
  const { data: verificationTemplate, included } = await SessionClientDashboard.postResourceCreate<
    VerificationTemplate,
    {
      type: ApiModel.VerificationTemplate;
      attributes: { builder: string; build_method: string };
    }
  >({
    type: ApiModel.VerificationTemplate,
    attributes: {
      builder: updatedVtmplv.type.split('/')[1].replace(/-/g, '_'),
      build_method: 'draft',
    },
  });
  const newVtmplvId = included?.find((entity): entity is VerificationTemplateVersion =>
    isVerificationTemplateVersion(entity),
  )?.id;

  if (!newVtmplvId) {
    throw new Error('VerificationTemplateVersion not found in included entities');
  }
  // pass-through the provided param vtmplv rather than the
  // newly created one in order to update with proper attributes
  return await saveVerificationTemplateVersion(
    verificationTemplate.id,
    set(['id'], newVtmplvId, updatedVtmplv),
  );
}

/**
 * Attempts to delete the VTV. This will only work if the VTV is the latest draft version
 * If the discard is successful, the id of the published version is returned
 */
export function discardDraftVerificationTemplateVersion(
  verificationTemplateVersionId: string,
): DashboardThunkAction<Promise<string>> {
  return async () => {
    const { data: publishedVersion } =
      await SessionClientDashboard.deleteResource<VerificationTemplateVersion>(
        ApiEndpoint.VerificationTemplateVersion,
        verificationTemplateVersionId,
      );
    return publishedVersion.id;
  };
}

export function publishVerificationTemplateVersion(
  verificationTemplateVersionId: string,
  type: VerificationTemplateVersion['type'],
): DashboardThunkAction {
  return async (dispatch) => {
    const { data: publishedVerificationTemplateVersion, included } =
      await SessionClientDashboard.postResourceAction<VerificationTemplateVersion>(
        {
          type: type,
          id: verificationTemplateVersionId,
        },
        'publish',
      );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const verificationTemplate = included.find((entity): entity is VerificationTemplate =>
      isVerificationTemplate(entity),
    );
    dispatch(
      setVerificationTemplateVersion({
        verificationTemplateVersion: publishedVerificationTemplateVersion,
      }),
    );
    dispatch(setVerificationTemplate({ verificationTemplate: verificationTemplate }));
  };
}

// If published, dup then save. Else just save.
export async function saveVerificationTemplateVersion(
  verificationTemplateId: string,
  updatedVtmplv: VerificationTemplateVersion,
): Promise<VerificationTemplateVersion> {
  const { id, type, attributes } = updatedVtmplv;
  const templateVersionId =
    attributes.status === VerificationTemplateVersionStatus.Published
      ? (
          await SessionClientDashboard.postResourceCreate<
            VerificationTemplateVersion,
            {
              type: VerificationTemplateVersion['type'];
              attributes: { 'verification-template-id': string };
            }
          >({
            type: type,
            attributes: {
              'verification-template-id': verificationTemplateId,
            },
          })
        ).data.id
      : id;

  const { data: verificationTemplateVersionUpdated } =
    await SessionClientDashboard.updateResource<VerificationTemplateVersion>({
      id: templateVersionId,
      type: type,
      attributes: postProcessVersion(attributes, type),
    });

  return verificationTemplateVersionUpdated;
}

/**
 * Verification template version specific processing of values to be compatible with the backend.
 */
export function postProcessVersion(
  attributes: VerificationTemplateVersion['attributes'],
  type: VerificationTemplateVersion['type'],
): VerificationTemplateVersion['attributes'] {
  switch (type) {
    case ApiModel.VerificationTemplateVersionGovernmentId: {
      const govIdAttributes = attributes as VerificationTemplateVersionGovernmentIdAttributes;
      return {
        ...govIdAttributes,
        'enabled-id-classes-by-country': Object.fromEntries(
          Object.entries(govIdAttributes['enabled-id-classes-by-country'])
            .map(([countryCode, configs]) => [
              countryCode,
              // Filter out all ID types where all sides are disabled
              configs.filter((config) => config['requires-sides'].length > 0),
            ])
            // Then, filter out countries where all ID types are disabled
            .filter(([, configs]) => configs.length > 0),
        ),
      };
    }
    default:
      return attributes;
  }
}

/**
 * helper to check verification template types when setting
 * vtmpl in redux after publishing vtmpl version
 */
function isVerificationTemplate(entity: ApiResource): entity is VerificationTemplate {
  return (
    entity.type === ApiModel.VerificationTemplate ||
    entity.type === ApiModel.VerificationTemplateAamva ||
    entity.type === ApiModel.VerificationTemplateBusinessWebsite ||
    entity.type === ApiModel.VerificationTemplateDatabase ||
    entity.type === ApiModel.VerificationTemplateDatabaseBusiness ||
    entity.type === ApiModel.VerificationTemplateDatabaseBusinessFootprint ||
    entity.type === ApiModel.VerificationTemplateDatabaseStandard ||
    entity.type === ApiModel.VerificationTemplateDatabaseEcbsv ||
    entity.type === ApiModel.VerificationTemplateDatabasePhoneCarrier ||
    entity.type === ApiModel.VerificationTemplateDatabaseSerpro ||
    entity.type === ApiModel.VerificationTemplateDatabaseTin ||
    entity.type === ApiModel.VerificationTemplateDatabaseVatNumber ||
    entity.type === ApiModel.VerificationTemplateDocument ||
    entity.type === ApiModel.VerificationTemplateEmailAddress ||
    entity.type === ApiModel.VerificationTemplateGovernmentId ||
    entity.type === ApiModel.VerificationTemplateGovernmentIdNfc ||
    entity.type === ApiModel.VerificationTemplateMobileDriversLicense ||
    entity.type === ApiModel.VerificationTemplatePhoneNumber ||
    entity.type === ApiModel.VerificationTemplateSelfie
  );
}

export const loadVerificationTemplateVersionDiff =
  (verificationTemplateId: string): DashboardThunkAction =>
  async (dispatch) => {
    const verificationTemplateVersionDiffPayload = (
      await SessionClientDashboard.getResource<
        VerificationTemplate,
        {
          'verification-template-version-diff': VerificationTemplateVersionDiff;
        }
      >(ApiEndpoint.VerificationTemplate, `${verificationTemplateId}/version-diff`)
    ).meta;
    const verificationTemplateVersionDiff =
      verificationTemplateVersionDiffPayload?.['verification-template-version-diff'];

    if (verificationTemplateVersionDiff != null)
      dispatch(
        setVerificationTemplateVersionDiff({
          verificationTemplateVersionDiff: verificationTemplateVersionDiff,
        }),
      );
  };
